/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import cx from "classnames";
import PerfectScrollbar from "perfect-scrollbar";

// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Hidden from "@material-ui/core/Hidden";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

// Iconos
import Person from "@material-ui/icons/Person";
import Edit from "@material-ui/icons/Edit";
import ExitToApp from "@material-ui/icons/ExitToApp";

// Contexto y rutas
import { useGlobal } from "GlobalContext";
import { sidebarRoutes } from "./sidebarRoutes";

// Assets
import backimg from "customAssets/background.png";
import logoIcon from "assets/img/logo.ico";

// Estilos
import sidebarStyle from "customAssets/customSidebarStyle";

// i18n
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(sidebarStyle);

/**
 * Wrapper del contenido navegable del sidebar.
 *
 * Se encarga de:
 * - activar PerfectScrollbar solo en desktop + Windows
 * - dejar scroll nativo en móvil
 * - actualizar el scrollbar cuando cambie el contenido
 */
function SidebarWrapper({
  className,
  user,
  headerLinks,
  links,
  enablePerfectScrollbar,
}) {
  const wrapperRef = React.useRef(null);
  const psRef = React.useRef(null);

  const isWindows =
    typeof navigator !== "undefined" &&
    navigator.platform &&
    navigator.platform.indexOf("Win") > -1;

  React.useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;

    if (enablePerfectScrollbar && isWindows) {
      if (!psRef.current) {
        psRef.current = new PerfectScrollbar(node, {
          suppressScrollX: true,
          suppressScrollY: false,
        });
      } else {
        psRef.current.update();
      }
      return;
    }

    if (psRef.current) {
      psRef.current.destroy();
      psRef.current = null;
    }
  }, [enablePerfectScrollbar, isWindows]);

  React.useEffect(() => {
    if (psRef.current) {
      psRef.current.update();
    }
  });

  React.useEffect(() => {
    return () => {
      if (psRef.current) {
        psRef.current.destroy();
        psRef.current = null;
      }
    };
  }, []);

  return (
    <div className={className} ref={wrapperRef}>
      {user}
      {headerLinks}
      {links}
    </div>
  );
}

function Sidebar(props) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  /**
   * miniActive local controla el efecto hover del sidebar mini en desktop.
   * Se mantiene el comportamiento actual:
   * - mini por defecto
   * - expandido al pasar el ratón
   */
  const [miniActive, setMiniActive] = React.useState(true);

  /**
   * Despliegue del panel de usuario.
   */
  const [openAvatar, setOpenAvatar] = React.useState(false);

  /**
   * Diálogo de logout.
   */
  const [logoutOpen, setLogoutOpen] = React.useState(false);

  /**
   * Estado global del usuario.
   */
  const { sharedValue, clearUserData } = useGlobal();
  const username = sharedValue?.username || "";
  const email = sharedValue?.email || "";
  const isLoggedIn = Boolean(email && email !== "");

  const { t } = useTranslation([
    "common",
    "myProfile",
    "editProfile",
    "logoutPage",
  ]);

  /**
   * Cierra el drawer móvil al navegar.
   */
  React.useEffect(() => {
    if (
      !isDesktop &&
      props.open &&
      typeof props.handleDrawerToggle === "function"
    ) {
      props.handleDrawerToggle();
    }

    setOpenAvatar(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoutConfirm = () => {
    history.push("/");
    clearUserData();
    setLogoutOpen(false);
    window.location.reload();
  };

  const activeRoute = (routePath) => {
    return location.pathname === routePath;
  };

  const createSidebarLinks = (routes) => {
    const { color, rtlActive } = props;

    return routes.map((route, key) => {
      const IconComponent = route.icon;

      const navLinkClasses =
        classes.itemLink +
        " " +
        cx({
          [" " + classes[color]]: activeRoute(route.path),
        });

      const itemText =
        classes.itemText +
        " " +
        cx({
          [classes.itemTextMini]: props.miniActive && miniActive,
          [classes.itemTextMiniRTL]:
            rtlActive && props.miniActive && miniActive,
          [classes.itemTextRTL]: rtlActive,
        });

      const itemIcon =
        classes.itemIcon +
        " " +
        cx({
          [classes.itemIconRTL]: rtlActive,
        });

      return (
        <ListItem key={key} className={classes.item}>
          <NavLink to={route.path} className={navLinkClasses}>
            <IconComponent className={itemIcon} />
            <ListItemText
              primary={t(route.labelKey)}
              disableTypography
              className={itemText}
            />
          </NavLink>
        </ListItem>
      );
    });
  };

  const { logoText, bgColor, rtlActive } = props;

  const itemText =
    classes.itemText +
    " " +
    cx({
      [classes.itemTextMini]: props.miniActive && miniActive,
      [classes.itemTextMiniRTL]: rtlActive && props.miniActive && miniActive,
      [classes.itemTextRTL]: rtlActive,
    });

  const collapseItemText =
    classes.collapseItemText +
    " " +
    cx({
      [classes.collapseItemTextMini]: props.miniActive && miniActive,
      [classes.collapseItemTextMiniRTL]:
        rtlActive && props.miniActive && miniActive,
      [classes.collapseItemTextRTL]: rtlActive,
    });

  const userWrapperClass =
    classes.user +
    " " +
    cx({
      [classes.whiteAfter]: bgColor === "white",
    });

  const caret =
    classes.caret +
    " " +
    cx({
      [classes.caretRTL]: rtlActive,
    });

  const collapseItemMini =
    classes.collapseItemMini +
    " " +
    cx({
      [classes.collapseItemMiniRTL]: rtlActive,
    });

  const photo =
    classes.photo +
    " " +
    cx({
      [classes.photoRTL]: rtlActive,
    });

  const userLoggedIn = (
    <div className={userWrapperClass}>
      <div className={photo}>
        <img
          src={logoIcon}
          alt="logo"
          className={classes.avatarImg}
          style={{ width: 48, height: 48 }}
        />
      </div>

      <List className={classes.list}>
        <ListItem className={classes.item + " " + classes.userItem}>
          <NavLink
            to="#"
            className={classes.itemLink + " " + classes.userCollapseButton}
            onClick={(e) => {
              e.preventDefault();
              setOpenAvatar((prev) => !prev);
            }}
          >
            <ListItemText
              primary={username}
              secondary={
                <b
                  className={
                    caret +
                    " " +
                    classes.userCaret +
                    " " +
                    (openAvatar ? classes.caretActive : "")
                  }
                />
              }
              disableTypography
              className={itemText + " " + classes.userItemText}
            />
          </NavLink>

          <Collapse in={openAvatar} unmountOnExit>
            <div style={{ padding: 4 }} />
            <List className={classes.list + " " + classes.collapseList}>
              <ListItem className={classes.collapseItem}>
                <NavLink
                  to="/profile"
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                >
                  <span className={collapseItemMini}>
                    <Person />
                  </span>
                  <ListItemText
                    primary={t("myprofile-card-title", { ns: "myProfile" })}
                    disableTypography
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>

              <ListItem className={classes.collapseItem}>
                <NavLink
                  to="/profile/edit"
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                >
                  <span className={collapseItemMini}>
                    <Edit />
                  </span>
                  <ListItemText
                    primary={t("editprofile-card-title", { ns: "editProfile" })}
                    disableTypography
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>

              <ListItem className={classes.collapseItem}>
                <NavLink
                  to="#"
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                  onClick={(e) => {
                    e.preventDefault();
                    setLogoutOpen(true);
                  }}
                >
                  <span className={collapseItemMini}>
                    <ExitToApp />
                  </span>
                  <ListItemText
                    primary={t("logout-dialog-title", { ns: "logoutPage" })}
                    disableTypography
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>
            </List>
          </Collapse>
        </ListItem>
      </List>
    </div>
  );

  const userLoggedOut = (
    <div className={userWrapperClass}>
      <div className={photo}>
        <img
          src={logoIcon}
          alt="logo"
          className={classes.avatarImg}
          style={{ width: 48, height: 48 }}
        />
      </div>

      <List className={classes.list}>
        <ListItem className={classes.item + " " + classes.userItem}>
          <NavLink
            to="#"
            className={classes.itemLink + " " + classes.userCollapseButton}
            onClick={(e) => {
              e.preventDefault();
              setOpenAvatar((prev) => !prev);
            }}
          >
            <ListItemText
              primary={t("anonimo", { ns: "common" })}
              secondary={
                <b
                  className={
                    caret +
                    " " +
                    classes.userCaret +
                    " " +
                    (openAvatar ? classes.caretActive : "")
                  }
                />
              }
              disableTypography
              className={itemText + " " + classes.userItemText}
            />
          </NavLink>

          <Collapse in={openAvatar} unmountOnExit>
            <div style={{ padding: 4 }} />
            <List className={classes.list + " " + classes.collapseList}>
              <ListItem className={classes.collapseItem}>
                <NavLink
                  to="/login"
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                >
                  <span className={collapseItemMini}>
                    <Person />
                  </span>
                  <ListItemText
                    primary={t("iniciar-sesion", { ns: "common" })}
                    disableTypography
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>

              <ListItem className={classes.collapseItem}>
                <NavLink
                  to="/register"
                  className={classes.itemLink + " " + classes.userCollapseLinks}
                >
                  <span className={collapseItemMini}>
                    <Edit />
                  </span>
                  <ListItemText
                    primary={t("registrarse", { ns: "common" })}
                    disableTypography
                    className={collapseItemText}
                  />
                </NavLink>
              </ListItem>
            </List>
          </Collapse>
        </ListItem>
      </List>
    </div>
  );

  const user = isLoggedIn ? userLoggedIn : userLoggedOut;

  const links = (
    <List className={classes.list}>
      {createSidebarLinks(sidebarRoutes)}
    </List>
  );

  const logoNormal =
    classes.logoNormal +
    " " +
    cx({
      [classes.logoNormalSidebarMini]: props.miniActive && miniActive,
      [classes.logoNormalSidebarMiniRTL]:
        rtlActive && props.miniActive && miniActive,
      [classes.logoNormalRTL]: rtlActive,
    });

  const logoMini =
    classes.logoMini +
    " " +
    cx({
      [classes.logoMiniRTL]: rtlActive,
    });

  const logoClasses =
    classes.logo +
    " " +
    cx({
      [classes.whiteAfter]: bgColor === "white",
    });

  const brand = (
    <div className={logoClasses}>
      {/*
      <a
        href="https://www.uned.es/"
        target="_blank"
        rel="noreferrer"
        className={logoMini}
      >
        <img src={props.logo} alt="logo" className={classes.img} />
      </a>
      <a
        href="https://www.uned.es/"
        target="_blank"
        rel="noreferrer"
        className={logoNormal}
      >
        {logoText}
      </a>
      */}
    </div>
  );

  const drawerPaper =
    classes.drawerPaper +
    " " +
    cx({
      [classes.drawerPaperMini]: props.miniActive && miniActive,
      [classes.drawerPaperRTL]: rtlActive,
    });

  const isWindows =
    typeof navigator !== "undefined" &&
    navigator.platform &&
    navigator.platform.indexOf("Win") > -1;

  const sidebarWrapper =
    classes.sidebarWrapper +
    " " +
    cx({
      [classes.drawerPaperMini]: props.miniActive && miniActive,
      [classes.sidebarWrapperWithPerfectScrollbar]: isDesktop && isWindows,
    });

  /**
   * El síntoma raro venía de aquí:
   * el Drawer móvil ahora abre con anchor a la izquierda,
   * pero el paper base del tema sigue trayendo posicionamiento lateral heredado.
   *
   * Forzamos explícitamente left/right en el paper del drawer móvil
   * para que animación y posición final apunten al mismo lado.
   */
  const mobileDrawerAnchor = rtlActive ? "right" : "left";
  const desktopDrawerAnchor = rtlActive ? "right" : "left";

  const mobilePaperStyle = rtlActive
    ? { left: "auto", right: 0 }
    : { left: 0, right: "auto" };

  const desktopPaperStyle = rtlActive
    ? { left: "auto", right: 0 }
    : { left: 0, right: "auto" };

  return (
    <div>
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        aria-labelledby="logout-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="logout-dialog-title">
          {t("logout-dialog-title", { ns: "logoutPage" })}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("logout-dialog-text", { ns: "logoutPage" })}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} color="default">
            {t("logout-dialog-cancel", { ns: "logoutPage" })}
          </Button>
          <Button onClick={handleLogoutConfirm} color="secondary">
            {t("logout-dialog-confirm", { ns: "logoutPage" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer móvil / tablet pequeña */}
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          anchor={mobileDrawerAnchor}
          open={props.open}
          classes={{
            paper: drawerPaper + " " + classes[bgColor + "Background"],
          }}
          PaperProps={{
            style: mobilePaperStyle,
          }}
          onClose={props.handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {brand}
          <SidebarWrapper
            className={sidebarWrapper}
            user={user}
            links={links}
            enablePerfectScrollbar={false}
          />
          <div
            className={classes.background}
            style={{ backgroundImage: `url(${backimg})` }}
          />
        </Drawer>
      </Hidden>

      {/* Drawer permanente desktop */}
      <Hidden smDown implementation="css">
        <Drawer
          anchor={desktopDrawerAnchor}
          variant="permanent"
          open
          onMouseOver={() => setMiniActive(false)}
          onMouseOut={() => setMiniActive(true)}
          classes={{
            paper: drawerPaper + " " + classes[bgColor + "Background"],
          }}
          PaperProps={{
            style: desktopPaperStyle,
          }}
        >
          {brand}
          <SidebarWrapper
            className={sidebarWrapper}
            user={user}
            links={links}
            enablePerfectScrollbar={true}
          />
          <div
            className={classes.background}
            style={{ backgroundImage: `url(${backimg})` }}
          />
        </Drawer>
      </Hidden>
    </div>
  );
}

Sidebar.defaultProps = {
  bgColor: "blue",
};

Sidebar.propTypes = {
  bgColor: PropTypes.oneOf(["white", "black", "blue"]),
  rtlActive: PropTypes.bool,
  color: PropTypes.oneOf([
    "white",
    "red",
    "orange",
    "green",
    "blue",
    "purple",
    "rose",
  ]),
  logo: PropTypes.string,
  logoText: PropTypes.string,
  image: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  miniActive: PropTypes.bool,
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

SidebarWrapper.propTypes = {
  className: PropTypes.string,
  user: PropTypes.node,
  headerLinks: PropTypes.node,
  links: PropTypes.node,
  enablePerfectScrollbar: PropTypes.bool,
};

export default Sidebar;